import { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import {
  BASIC_DARK_GRAY,
  BASIC_LIGHT_GRAY,
  BASIC_WHITE,
  BASIC_BROWN,
  BASIC_GRAY_BACKGROUND,
} from "../../../constants/colors";

type Props = {
  initialCount: number;
  onChange: (count: number) => void;
  size?: "sm" | "md";
  disabled?: boolean;
  limited?: boolean;
  limitedDisabled?: boolean;
  shouldReset?: boolean;
};

export const MenuCountSelect: React.FC<Props> = ({
  initialCount,
  onChange,
  size = "md",
  disabled,
  limited,
  limitedDisabled,
  shouldReset,
}) => {
  const [count, setCount] = useState<number>(initialCount);

  useEffect(() => {
    if (shouldReset) setCount(initialCount);
  }, [shouldReset]);

  const handleSetCount = useCallback(
    (add: boolean) => {
      if (disabled || limited) return;
      if (add && count === 99) return;
      if (!add && count === 0) return;

      if (add) {
        onChange(count + 1);
        setCount(count + 1);
      } else {
        onChange(count - 1);
        setCount(count - 1);
      }
    },
    [count, onChange, limitedDisabled, disabled],
  );

  return (
    <MenuCountSelectContainer disabled={(disabled ?? false) && !limited}>
      <MenuCountButton
        size={size}
        disabled={(disabled ?? false) || (limited ?? false)}
        onClick={() => handleSetCount(false)}
      >
        -
      </MenuCountButton>
      <MenuCounter
        size="sm"
        type="text"
        readOnly
        value={count}
        disabled={disabled}
      />
      <MenuCountButton
        disabled={(disabled ?? false) || (limited ?? false) || count === 99}
        size={size}
        onClick={() => handleSetCount(true)}
      >
        +
      </MenuCountButton>
    </MenuCountSelectContainer>
  );
};

const MenuCountSelectContainer = styled.div<{ disabled: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 5px;
  opacity: ${(props) => (props.disabled ? 0.25 : 1)};
`;

const MenuCountButton = styled.div<{ disabled: boolean; size: "sm" | "md" }>`
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  opacity: ${(props) => (props.disabled ? 0.25 : 1)};
  width: ${(props) => (props.size === "sm" ? "32px" : "50px")};
  text-align: center;
  padding: 5px 0;
  margin: 0;
  background-color: ${BASIC_GRAY_BACKGROUND};
  box-sizing: border-box;
  height: ${(props) => (props.size === "sm" ? "32px" : "50px")};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: 500;
  color: #502700;
  border: 1px solid #502700;
  background-color: ${BASIC_WHITE};
  user-select: none;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${BASIC_LIGHT_GRAY};
    transform: scale(1.05);
  }

  /*&:first-of-type {
    border-left: 1px solid ${BASIC_DARK_GRAY};
    transform: scale(1.05);
  }*/

  &:last-of-type {
    background-color: ${BASIC_BROWN};
    color: ${BASIC_WHITE};
    border-color: ${BASIC_BROWN};

    &:hover {
      background-color: ${BASIC_BROWN};
      border-color: ${BASIC_BROWN};
    }
  }
`;

const MenuCounter = styled.input<{ size: "sm" | "md" }>`
  font-size: 18px;
  font-weight: 500;
  outline: none;
  width: ${(props) => (props.size === "sm" ? "32px" : "50px")};
  text-align: center;
  padding: 5px 0;
  margin: 0;
  border-left: none;
  border-right: none;
  border: none;
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &[type="number"] {
    -moz-appearance: textfield;
    appearance: textfield;
  }
`;
